package ai.wagemix.logic.analytics

import ai.wagemix.logic.WMRoutes
import ai.wagemix.logic.authActionModeOf
import ai.wagemix.logic.entityIdOf
import ai.wagemix.logic.patternOf
import ai.wagemix.logic.screenOf
import kotlin.time.Duration.Companion.minutes
import kotlin.time.Instant

/** The campaign parameters of the URL a visit landed on (`utm_*`); each is null when absent. */
interface WMUtm {
    val source: String?
    val medium: String?
    val campaign: String?
    val content: String?
    val term: String?

    /** Anchors [NONE]. */
    companion object {

        /** No campaign parameters. */
        val NONE: WMUtm get() = WMUtm(source = null, medium = null, campaign = null, content = null, term = null)
    }
}

/**
 * Creates a [WMUtm].
 *
 * @return the campaign parameters; a blank value is treated as absent
 */
fun WMUtm(source: String?, medium: String?, campaign: String?, content: String?, term: String?): WMUtm {
    return WMUtmData(
        source = source?.ifBlank { null },
        medium = medium?.ifBlank { null },
        campaign = campaign?.ifBlank { null },
        content = content?.ifBlank { null },
        term = term?.ifBlank { null },
    )
}

private data class WMUtmData(
    override val source: String?,
    override val medium: String?,
    override val campaign: String?,
    override val content: String?,
    override val term: String?,
): WMUtm

/**
 * One visit, in the sense of OpenPanel's sessions: screen views of one device with less than [WMAnalyticsVisit.TIMEOUT]
 * between them. The client keeps the current one in persistent storage, so it spans restarts.
 */
interface WMAnalyticsVisit {

    /** When the visit's last screen view happened. */
    val lastActivityAt: Instant

    /** The screen name of the visit's last screen view. */
    val screen: String

    /** The campaign parameters the visit landed with, carried on each of its screen views. */
    val utm: WMUtm

    /** Anchors the constants. */
    companion object {

        /** The inactivity after which the next screen view starts a new visit, as OpenPanel's sessions do. */
        val TIMEOUT get() = 30.minutes
    }
}

/**
 * Creates a [WMAnalyticsVisit].
 *
 * @return the visit
 */
fun WMAnalyticsVisit(lastActivityAt: Instant, screen: String, utm: WMUtm): WMAnalyticsVisit {
    return WMAnalyticsVisitData(lastActivityAt = lastActivityAt, screen = screen, utm = utm)
}

private data class WMAnalyticsVisitData(
    override val lastActivityAt: Instant,
    override val screen: String,
    override val utm: WMUtm,
): WMAnalyticsVisit

/** The properties of one `screen_view` (planning/analytics-plan.md §5, screen views). */
interface WMScreenView {

    /** The route pattern (`__path`). */
    val pattern: String

    /** The stable screen name (`screen`). */
    val screen: String

    /** The mix or shared mix id the route shows (`entity_id`), or null. */
    val entityId: String?

    /** What an `/auth-action` link does (`auth_action_mode`), or null. */
    val authActionMode: String?

    /** The previous screen of the visit (`previous_screen`), or null on its first screen. */
    val previousScreen: String?

    /** How long the previous screen was shown (`ms_on_previous_screen`), or null on the visit's first screen. */
    val msOnPreviousScreen: Long?

    /** The visit's campaign parameters (`utm_*`). */
    val utm: WMUtm

    /** The external page that sent the visitor (`__referrer`), only on the visit's first screen. */
    val referrer: String?

    /** Whether this is the visit's first screen (`is_entry`). */
    val isEntry: Boolean
}

/** A screen view and the visit to store after it. */
interface WMScreenViewStep {
    val screenView: WMScreenView
    val visit: WMAnalyticsVisit

    /** Anchors [of]. */
    companion object {

        /**
         * Decides the properties of the screen view of [path], continuing [stored] when it is recent enough.
         *
         * A new visit (none stored, or the last activity longer than [WMAnalyticsVisit.TIMEOUT] ago) takes its
         * campaign parameters from [landingUtm] and reports [referrer] when it is another site's page. A continuing
         * visit carries its stored campaign parameters and reports the screen it leaves.
         *
         * @param stored the visit the client stored, or null
         * @param path the route path, with its query string
         * @param landingUtm the campaign parameters of the current URL
         * @param referrer the document's referrer, or null or blank when there is none
         * @param siteOrigin this site's origin (`https://wagemix.ai`), to tell an external referrer from an internal
         * one
         * @param now the time of the screen view
         * @return the screen view and the visit to store
         */
        fun of(
            stored: WMAnalyticsVisit?,
            path: String,
            landingUtm: WMUtm,
            referrer: String?,
            siteOrigin: String,
            now: Instant,
        ): WMScreenViewStep {
            val screen = WMRoutes.screenOf(path)
            val continuing = stored?.takeIf { now - it.lastActivityAt < WMAnalyticsVisit.TIMEOUT }
            val utm = continuing?.utm ?: landingUtm
            val screenView = WMScreenViewData(
                pattern = WMRoutes.patternOf(path),
                screen = screen,
                entityId = WMRoutes.entityIdOf(path),
                authActionMode = WMRoutes.authActionModeOf(path),
                previousScreen = continuing?.screen,
                msOnPreviousScreen = continuing?.let { (now - it.lastActivityAt).inWholeMilliseconds },
                utm = utm,
                referrer = if (continuing == null) externalReferrerOf(referrer, siteOrigin) else null,
                isEntry = continuing == null,
            )
            return WMScreenViewStepData(screenView = screenView, visit = WMAnalyticsVisit(now, screen, utm))
        }
    }
}

private data class WMScreenViewData(
    override val pattern: String,
    override val screen: String,
    override val entityId: String?,
    override val authActionMode: String?,
    override val previousScreen: String?,
    override val msOnPreviousScreen: Long?,
    override val utm: WMUtm,
    override val referrer: String?,
    override val isEntry: Boolean,
): WMScreenView

private data class WMScreenViewStepData(
    override val screenView: WMScreenView,
    override val visit: WMAnalyticsVisit,
): WMScreenViewStep

/** [referrer] without its query string and fragment when it is another origin's page, otherwise null. */
private fun externalReferrerOf(referrer: String?, siteOrigin: String): String? {
    val page = referrer?.substringBefore('#')?.substringBefore('?')?.ifBlank { null } ?: return null
    return if (originOf(page) == originOf(siteOrigin)) null else page
}

/** `scheme://host[:port]` of [url], lower-cased; the whole value when it has no scheme. */
private fun originOf(url: String): String {
    val schemeEnd = url.indexOf("://")
    if (schemeEnd < 0) {
        return url.lowercase()
    }
    val hostEnd = url.indexOf('/', startIndex = schemeEnd + 3).takeIf { it >= 0 } ?: url.length
    return url.substring(0, hostEnd).lowercase()
}
